import { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo } from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router'

import { useSegment } from '../../../../../hooks'
import { Paging } from '../../../../../types/metable'
import { getOrganizationViewUrl } from '../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../utils/routing/reactRoute'
import { InvoiceDocumentType } from '../../../enums/invoiceDocumentType'
import { QuoteStatus } from '../../../enums/quoteStatus'
import { useFetchSalesDocuments } from '../../../hooks/useFetchSalesDocuments'
import { useHasQuotes } from '../../../hooks/useHasQuotes'
import { getInvoiceDocumentRouteBase } from '../../../utils/getInvoiceDocumentRouteBase'
import { getQuoteQueryKey } from '../../../utils/getQuoteQueryKey'
import { QuotesTableRow } from '../types/quotesTableRow'
import { getQuoteQueryData } from '../utils/getQuoteQueryData'
import { getQuotesTableData } from '../utils/getQuotesTableData'
import { getTrackingEvents } from '../utils/getTrackingEvents'
import { useQuotesList } from './quotesList'
import { useQuotesListFilters } from './quotesListFilters'

interface ContextState {
  data: QuotesTableRow[]
  isLoading: boolean
  hasQuotes: boolean
  onSelectRow: (quote: QuotesTableRow) => void
  pagination?: Paging
}

const QuotesListDataContext = createContext<ContextState | undefined>(undefined)

interface QuotesListDataProviderProps {
  children?: ReactNode
}
const quoteDocumentRouteBase = getInvoiceDocumentRouteBase(InvoiceDocumentType.Quote)

export const QuotesListDataProvider = ({ children }: QuotesListDataProviderProps): ReactElement => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { track } = useSegment()
  const { options, organization, tableId } = useQuotesList()
  const [{ customerId, page, pageSize, period, searchQuery, sortProperty, sortDirection, status }] =
    useQuotesListFilters()
  const { hasQuotes } = useHasQuotes({ customerId, enabled: options.isFetchEnabled })
  const organizationUrl = organization?.url

  const {
    documents: quotes,
    isLoading,
    pagination,
  } = useFetchSalesDocuments({
    customerId,
    documentsListType: 'quotes',
    isEnabled: options.isFetchEnabled,
    page,
    pageSize,
    period,
    searchQuery,
    sortProperty,
    sortDirection,
    status,
  })

  const quotesData = useMemo(() => {
    const activeQuotes = quotes.filter((quote) => quote.status?.status !== QuoteStatus.Deleted)
    return getQuotesTableData(activeQuotes, organizationUrl)
  }, [organizationUrl, quotes])

  const onSelectRow = useCallback(
    (quote: QuotesTableRow) => {
      const { documentType, id: quoteId } = quote
      const quoteQueryData = getQuoteQueryData(quoteId, quotes)
      const trackingEvents = getTrackingEvents(tableId)

      queryClient.setQueryData(getQuoteQueryKey(quoteId), quoteQueryData)

      if (trackingEvents?.rowClicked) {
        track(trackingEvents.rowClicked, { documentId: quoteId, documentType })
      }

      if (documentType === InvoiceDocumentType.Quote) {
        navigate(
          `${getOrganizationViewUrl(organizationUrl, reactRoute.organization.quotes.getViewQuoteRoute(quoteId))}`,
        )
      }
      if (documentType === InvoiceDocumentType.QuoteDraft) {
        navigate(
          `${getOrganizationViewUrl(organizationUrl, reactRoute.organization.invoices.getEditDocumentDraftRoute(quoteDocumentRouteBase, quoteId))}`,
        )
      }
    },
    [navigate, organizationUrl, queryClient, quotes, tableId, track],
  )

  const value = useMemo(
    () => ({
      data: quotesData,
      isLoading,
      hasQuotes,
      onSelectRow,
      pagination,
    }),
    [hasQuotes, isLoading, onSelectRow, pagination, quotesData],
  )

  return <QuotesListDataContext.Provider value={value}>{children}</QuotesListDataContext.Provider>
}

export const useQuotesListData = (): ContextState => {
  const context = useContext(QuotesListDataContext)

  if (!context) {
    throw new Error('QuotesListDataContext is missing in the module!')
  }

  return context
}
